import { useEffect, useState, useMemo, useCallback } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import {
  Plus, Trash2, Edit2, Sparkles, Image as ImageIcon, Loader2, Search, Eye, ExternalLink,
  Copy, LayoutGrid, List as ListIcon, RefreshCw, Calendar, TrendingUp, Zap, FileText, Filter, Wand2,
} from "lucide-react";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", category: "Guides", tags: [], read_time: 4, published: true, hero_image: "" };

function slugify(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
}
function wordCount(t) { return (t || "").trim().split(/\s+/).filter(Boolean).length; }
function readMinutes(t) { return Math.max(1, Math.round(wordCount(t) / 200)); }
function seoScore(post) {
  let s = 0;
  const tips = [];
  if ((post.title || "").length >= 40 && (post.title || "").length <= 60) s += 25; else tips.push("Title 40–60 chars");
  if ((post.excerpt || "").length >= 120 && (post.excerpt || "").length <= 160) s += 25; else tips.push("Excerpt 120–160 chars");
  if (/^##\s|^###\s/m.test(post.content || "")) s += 25; else tips.push("Add ## H2 sub-headings");
  if (wordCount(post.content) >= 600) s += 25; else tips.push("At least 600 words");
  return { score: s, tips };
}

function fmtRelative(iso) {
  if (!iso) return "—";
  if (iso === "next-check") return "within 6h";
  const diff = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(diff);
  const suffix = diff > 0 ? "in" : "";
  const ago = diff > 0 ? "" : "ago";
  let v, u;
  if (abs < 60) { v = abs; u = "s"; }
  else if (abs < 3600) { v = Math.round(abs / 60); u = "m"; }
  else if (abs < 86400) { v = Math.round(abs / 3600); u = "h"; }
  else { v = Math.round(abs / 86400); u = "d"; }
  return `${suffix} ${v}${u} ${ago}`.trim();
}

// Simple markdown → HTML for preview pane
function renderMd(md) {
  if (!md) return "";
  return md
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/### (.*)/g, '<h3 class="text-lg font-black text-slate-900 mt-4">$1</h3>')
    .replace(/## (.*)/g, '<h2 class="text-xl font-black text-slate-900 mt-5">$1</h2>')
    .replace(/^- (.*)$/gm, '<li class="text-slate-700">$1</li>')
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, (m) => `<ul class="list-disc pl-6 my-3">${m}</ul>`)
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-black">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-indigo-600 underline">$1</a>')
    .split(/\n\n+/).map((p) => p.startsWith("<") ? p : `<p class="text-slate-700 leading-relaxed my-2">${p.replace(/\n/g, "<br/>")}</p>`).join("\n");
}

export default function AdminBlogManager() {
  const [items, setItems] = useState([]);
  const [roadmap, setRoadmap] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | id | null
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all / published / draft
  const [view, setView] = useState("list"); // list | grid
  const [preview, setPreview] = useState(false);
  const [genBusy, setGenBusy] = useState(null);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([
      adminApi.get("/admin/blog-posts"),
      adminApi.get("/admin/blog-posts/roadmap").catch(() => ({ data: null })),
    ]);
    setItems(a.data || []);
    setRoadmap(b.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const generate = async (tier) => {
    setGenBusy(tier);
    toast.message(`Starting ${tier}… Claude is writing + Nano Banana is painting the cover. Post appears in ~60-90s.`);
    try {
      const r = await adminApi.post("/admin/blog-posts/generate", { tier, publish: true });
      const msg = r.data?.message || `Queued: ${r.data?.title || tier}`;
      toast.success(msg);
      // Auto-refresh the list every 15s for the next 2 min so the new post
      // appears without the admin having to hit refresh manually.
      let attempts = 0;
      const iv = setInterval(async () => {
        attempts++;
        await load();
        if (attempts >= 8) clearInterval(iv);
      }, 15000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed"); }
    finally { setGenBusy(null); }
  };

  const cleanup = async () => {
    if (!window.confirm("This will DELETE any placeholder posts (titles like 'TEST Blog') AND any posts without a cover image. Continue?")) return;
    setGenBusy("cleanup");
    try {
      const r = await adminApi.post("/admin/blog-posts/cleanup", { delete_test: true, delete_coverless: true });
      toast.success(`Removed ${r.data.deleted} old posts. ${r.data.remaining} remaining.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cleanup failed"); }
    finally { setGenBusy(null); }
  };

  const dedupe = async () => {
    setGenBusy("dedupe-preview");
    let preview;
    try {
      preview = await adminApi.post("/admin/blog-posts/dedupe", { dry_run: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dedupe preview failed");
      setGenBusy(null);
      return;
    }
    const count = preview.data?.count || 0;
    setGenBusy(null);
    if (count === 0) { toast.success("No duplicate posts found"); return; }
    const list = (preview.data.would_delete || []).slice(0, 10).map((d) => `• ${d.slug} → keeps: ${d.kept_slug}`).join("\n");
    if (!window.confirm(`Delete ${count} duplicate blog post(s)?\n\n${list}${count > 10 ? "\n...and more" : ""}`)) return;
    setGenBusy("dedupe");
    try {
      const r = await adminApi.post("/admin/blog-posts/dedupe");
      toast.success(`Removed ${r.data.deleted} duplicates. ${r.data.remaining} posts remaining.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dedupe failed"); }
    finally { setGenBusy(null); }
  };


  const backfillHeroes = async () => {
    if (!window.confirm("Regenerate cover images for every post whose image file is missing (404)? Uses Nano Banana — one image per post, may take a few minutes.")) return;
    setGenBusy("backfill");
    try {
      const r = await adminApi.post("/admin/blog-posts/backfill-heroes");
      toast.success(r.data?.message || `Queued ${r.data?.queued_count || 0} regenerations.`);
      // Poll for updates every 20s for the next 4 minutes.
      let attempts = 0;
      const iv = setInterval(async () => {
        attempts++;
        await load();
        if (attempts >= 12) clearInterval(iv);
      }, 20000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
    finally { setGenBusy(null); }
  };

  const regenHero = async (p) => {
    if (!window.confirm(`Regenerate the cover image for "${p.title}"? This calls Nano Banana and takes ~15s.`)) return;
    setGenBusy(`regen-${p.id}`);
    try {
      const r = await adminApi.post(`/admin/blog-posts/${p.id}/regenerate-hero`);
      toast.success("New cover generated");
      // Optimistically update the local item so the thumbnail refreshes immediately
      setItems((prev) => prev.map((it) => it.id === p.id ? { ...it, hero_image: r.data.hero_image, cover_image: r.data.cover_image } : it));
    } catch (e) { toast.error(e?.response?.data?.detail || "Regenerate failed"); }
    finally { setGenBusy(null); }
  };

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ ...EMPTY, ...p, tags: Array.isArray(p.tags) ? p.tags : [] });
    setPreview(false);
  };
  const startNew = () => { setEditing("new"); setForm(EMPTY); setPreview(false); };

  const save = async () => {
    if (!form.title || !form.content) return toast.error("Title & content required");
    const payload = {
      ...form,
      slug: form.slug || slugify(form.title),
      tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags,
      read_time: Number(form.read_time) || readMinutes(form.content),
    };
    try {
      if (editing === "new") await adminApi.post("/admin/blog-posts", payload);
      else await adminApi.put(`/admin/blog-posts/${editing}`, payload);
      toast.success("Saved");
      setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await adminApi.delete(`/admin/blog-posts/${p.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };
  const togglePublished = async (p) => {
    try {
      await adminApi.put(`/admin/blog-posts/${p.id}`, { published: !p.published });
      toast.success(p.published ? "Unpublished" : "Published");
      load();
    } catch { toast.error("Failed"); }
  };
  const copyUrl = (p) => {
    const url = `${window.location.origin}/blog/${p.slug}`;
    navigator.clipboard?.writeText(url);
    toast.success("URL copied");
  };

  const filtered = useMemo(() => {
    let list = [...items];
    if (filter === "published") list = list.filter((p) => p.published);
    else if (filter === "draft") list = list.filter((p) => !p.published);
    if (q) {
      const ql = q.toLowerCase();
      list = list.filter((p) => (p.title || "").toLowerCase().includes(ql) || (p.slug || "").toLowerCase().includes(ql) || (p.category || "").toLowerCase().includes(ql));
    }
    return list;
  }, [items, filter, q]);

  const stats = useMemo(() => ({
    total: items.length,
    published: items.filter((p) => p.published).length,
    draft: items.filter((p) => !p.published).length,
    views: items.reduce((a, p) => a + (p.views || 0), 0),
  }), [items]);

  const seo = seoScore(form);

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 md:p-8" data-testid="admin-blog">

        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600">Blog CMS</span>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-1" data-testid="admin-blog-title">Content &amp; SEO</h1>
            <p className="text-slate-500 mt-1 text-sm">Manage articles, run the SEO roadmap, and auto-draft pillars &amp; spokes.</p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={dedupe} disabled={!!genBusy} data-testid="blog-dedupe"
              title="Find & delete near-duplicate blog posts (keeps oldest)"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 text-xs font-black disabled:opacity-50">
              {genBusy === "dedupe" || genBusy === "dedupe-preview" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Remove duplicates
            </button>
            <button onClick={backfillHeroes} disabled={!!genBusy} data-testid="blog-backfill-heroes"
              title="Regenerate cover images for every post whose file is missing"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 text-xs font-black disabled:opacity-50">
              {genBusy === "backfill" ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />} Backfill missing covers
            </button>
            <button onClick={cleanup} disabled={!!genBusy} data-testid="blog-cleanup"
              title="Delete TEST posts and posts without a cover image"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 text-xs font-black disabled:opacity-50">
              {genBusy === "cleanup" ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Clean up
            </button>
            <button onClick={() => generate("spoke")} disabled={!!genBusy} data-testid="blog-gen-spoke"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-black disabled:opacity-50">
              {genBusy === "spoke" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate spoke
            </button>
            <button onClick={() => generate("pillar")} disabled={!!genBusy} data-testid="blog-gen-pillar"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black disabled:opacity-50">
              {genBusy === "pillar" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generate pillar
            </button>
            <button onClick={startNew} data-testid="blog-add"
              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-black">
              <Plus size={13} /> New post
            </button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MiniStat label="TOTAL POSTS" value={stats.total} icon={FileText} tone="slate" />
          <MiniStat label="PUBLISHED" value={stats.published} icon={TrendingUp} tone="emerald" />
          <MiniStat label="DRAFTS" value={stats.draft} icon={Edit2} tone="amber" />
          <MiniStat label="TOTAL VIEWS" value={stats.views} icon={Eye} tone="fuchsia" />
        </div>

        {/* Roadmap */}
        {roadmap && (
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid="blog-roadmap">
            <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
              <div>
                <h2 className="font-black text-slate-900">SEO roadmap</h2>
                <p className="text-xs text-slate-500 mt-0.5">Auto-publishes 1 spoke every 3 days. Pillars are admin-triggered.</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-black text-emerald-700 tabular-nums">
                  {roadmap.published}<span className="text-slate-400">/{roadmap.total}</span>
                </div>
                <div className="text-[10px] font-black uppercase text-slate-500 tracking-wider">published</div>
              </div>
            </div>

            {roadmap.scheduler && (
              <div className={`flex flex-wrap items-stretch gap-3 mb-4 p-1 rounded-2xl border ${roadmap.scheduler.enabled ? "border-emerald-100 bg-emerald-50/40" : "border-amber-100 bg-amber-50/40"}`}>
                <div className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl border border-slate-100">
                  <span className={`relative inline-flex w-2.5 h-2.5 rounded-full ${roadmap.scheduler.enabled ? "bg-emerald-500" : "bg-amber-500"}`}>
                    {roadmap.scheduler.enabled && <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />}
                  </span>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Scheduler</div>
                    <div className={`text-sm font-black ${roadmap.scheduler.enabled ? "text-emerald-700" : "text-amber-700"}`}>
                      {roadmap.scheduler.enabled ? "Auto-publishing" : "Paused"}
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-[180px] flex items-center px-4 py-2.5 bg-white rounded-xl border border-slate-100">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Last published</div>
                    <div className="text-sm font-black text-slate-900 truncate">{roadmap.scheduler.last_published_title || "—"}</div>
                    <div className="text-[11px] text-slate-500">{fmtRelative(roadmap.scheduler.last_published_at)}</div>
                  </div>
                </div>
                <div className="flex-1 min-w-[180px] flex items-center px-4 py-2.5 bg-white rounded-xl border border-slate-100">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">Next in queue</div>
                    <div className="text-sm font-black text-slate-900 truncate">{roadmap.upcoming?.[0]?.title || "Queue empty 🎉"}</div>
                    {roadmap.upcoming?.[0] && <div className="text-[11px] text-slate-500">Publishes within 3 days</div>}
                  </div>
                </div>
              </div>
            )}

            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-gradient-to-r from-emerald-500 via-fuchsia-500 to-sky-500 rounded-full transition-all"
                style={{ width: `${(roadmap.published / Math.max(roadmap.total, 1)) * 100}%` }} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <RoadmapTile label="Pillars" done={roadmap.pillars_done} total={roadmap.pillars_total} color="text-emerald-700" />
              <RoadmapTile label="Spokes" done={roadmap.spokes_done} total={roadmap.spokes_total} color="text-sky-700" />
              <RoadmapTile label="Remaining" done={roadmap.remaining} total={roadmap.total} color="text-amber-600" />
              <RoadmapTile label="Clusters" done={roadmap.clusters.filter((c) => c.pillar_published).length} total={roadmap.clusters.length} color="text-violet-600" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-wider text-slate-500 mb-2">Up next</div>
              <ul className="space-y-1.5">
                {roadmap.upcoming.slice(0, 6).map((t) => (
                  <li key={t.slug} className="flex items-center justify-between gap-2 text-sm" data-testid={`upcoming-${t.slug}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-[9px] uppercase tracking-wider font-black px-2 py-0.5 rounded ${t.tier === "pillar" ? "bg-emerald-100 text-emerald-700" : "bg-sky-100 text-sky-700"}`}>{t.tier}</span>
                      <span className="truncate text-slate-700">{t.title}</span>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap hidden md:inline">{t.cluster}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Editor */}
        {editing && (
          <div className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="blog-editor">
            <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600">{editing === "new" ? "New post" : "Edit"}</span>
                <span className="text-sm font-black text-slate-900">{form.title || "Untitled"}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-lg border border-slate-200 p-1">
                  <button onClick={() => setPreview(false)} className={`px-3 h-8 rounded-md text-xs font-black ${!preview ? "bg-slate-900 text-white" : "text-slate-500"}`}>Edit</button>
                  <button onClick={() => setPreview(true)} className={`px-3 h-8 rounded-md text-xs font-black ${preview ? "bg-slate-900 text-white" : "text-slate-500"}`}>Preview</button>
                </div>
                <button onClick={() => setEditing(null)} className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-bold">Cancel</button>
                <button onClick={save} data-testid="blog-save" className="h-9 px-4 rounded-lg bg-gradient-to-r from-indigo-500 to-indigo-700 text-white text-xs font-black">Save</button>
              </div>
            </div>

            <div className={`grid ${preview ? "md:grid-cols-2" : "grid-cols-1"} gap-4 p-5`}>
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Title"><input className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} data-testid="blog-title" /></Field>
                  <Field label="Slug"><input className="inp font-mono" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} data-testid="blog-slug" /></Field>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Category"><input className="inp" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></Field>
                  <Field label={`Read time · auto=${readMinutes(form.content)} min`}>
                    <input type="number" className="inp" value={form.read_time} onChange={(e) => setForm({ ...form, read_time: e.target.value })} />
                  </Field>
                </div>
                <Field label={`Excerpt (${(form.excerpt || "").length}/160)`}>
                  <input className="inp" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
                </Field>
                <Field label={`Content · ${wordCount(form.content)} words · ${readMinutes(form.content)} min read`}>
                  <textarea rows={14} className="inp h-auto py-3 resize-none font-mono text-sm" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="blog-content" />
                </Field>
                <Field label="Tags (comma separated)">
                  <input className="inp" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
                </Field>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-sm font-bold text-slate-700">
                    <input type="checkbox" checked={!!form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="rounded" /> Published
                  </label>
                  <div className="ml-auto flex items-center gap-3">
                    <div>
                      <div className="text-[10px] font-black uppercase text-slate-500 tracking-wider">SEO score</div>
                      <div className={`text-lg font-black tabular-nums ${seo.score >= 75 ? "text-emerald-600" : seo.score >= 50 ? "text-amber-600" : "text-rose-600"}`}>{seo.score}/100</div>
                    </div>
                    {seo.tips.length > 0 && <div className="text-[10px] text-slate-500 max-w-[160px]">Fix: {seo.tips.join(", ")}</div>}
                  </div>
                </div>
              </div>

              {preview && (
                <div className="border-l border-slate-100 pl-5">
                  <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mb-2">Preview</div>
                  <article className="prose prose-slate max-w-none">
                    <h1 className="text-2xl font-black text-slate-900">{form.title || "Untitled"}</h1>
                    {form.excerpt && <p className="text-slate-500 mt-2 italic">{form.excerpt}</p>}
                    <div className="mt-4 text-sm" dangerouslySetInnerHTML={{ __html: renderMd(form.content) }} />
                  </article>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Toolbar for list */}
        <div className="mt-4 bg-white border border-slate-200 rounded-2xl shadow-sm p-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, slug, or category…" data-testid="blog-search"
              className="w-full h-10 pl-10 pr-3 rounded-lg bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300" />
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            {[{ v: "all", label: "All" }, { v: "published", label: "Published" }, { v: "draft", label: "Drafts" }].map((f) => (
              <button key={f.v} onClick={() => setFilter(f.v)} data-testid={`blog-filter-${f.v}`}
                className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-black transition ${filter === f.v ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800"}`}>
                <Filter size={11} /> {f.label}
              </button>
            ))}
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            <button onClick={() => setView("list")} data-testid="blog-view-list" className={`p-1.5 rounded-md ${view === "list" ? "bg-slate-900 text-white" : "text-slate-500"}`}><ListIcon size={14} /></button>
            <button onClick={() => setView("grid")} data-testid="blog-view-grid" className={`p-1.5 rounded-md ${view === "grid" ? "bg-slate-900 text-white" : "text-slate-500"}`}><LayoutGrid size={14} /></button>
          </div>
          <button onClick={load} data-testid="blog-refresh" className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        {/* List / Grid */}
        {view === "list" ? (
          <div className="mt-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-100">
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="text-left px-5 py-3 font-black">Title</th>
                    <th className="text-left px-3 py-3 font-black">Category</th>
                    <th className="text-left px-3 py-3 font-black">Status</th>
                    <th className="text-right px-3 py-3 font-black">Read</th>
                    <th className="text-right px-3 py-3 font-black">Views</th>
                    <th className="text-left px-3 py-3 font-black">Updated</th>
                    <th className="text-right px-5 py-3 font-black">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`blog-row-${p.slug}`}>
                      <td className="px-5 py-3">
                        <div className="font-black text-slate-900">{p.title}</div>
                        <div className="text-[11px] font-mono text-slate-500 mt-0.5">/blog/{p.slug}</div>
                      </td>
                      <td className="px-3 py-3"><span className="text-xs text-slate-600">{p.category}</span></td>
                      <td className="px-3 py-3">
                        <button onClick={() => togglePublished(p)} data-testid={`blog-toggle-${p.slug}`}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-black transition ${p.published ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                          {p.published ? "PUBLISHED" : "DRAFT"}
                        </button>
                      </td>
                      <td className="px-3 py-3 text-right text-xs text-slate-600 tabular-nums">{p.read_time || "—"} min</td>
                      <td className="px-3 py-3 text-right font-mono text-slate-700 tabular-nums">{p.views || 0}</td>
                      <td className="px-3 py-3 text-xs text-slate-500">{fmtRelative(p.updated_at || p.created_at)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" title="View live" className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><ExternalLink size={13} /></a>
                          <button onClick={() => copyUrl(p)} title="Copy URL" className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Copy size={13} /></button>
                          <button onClick={() => regenHero(p)} disabled={genBusy === `regen-${p.id}`} title="Regenerate cover image (Nano Banana)" data-testid={`blog-regen-hero-${p.slug}`} className="p-2 rounded-lg text-fuchsia-600 hover:text-white hover:bg-fuchsia-500 disabled:opacity-40">
                            {genBusy === `regen-${p.id}` ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
                          </button>
                          <button onClick={() => startEdit(p)} title="Edit" data-testid={`blog-edit-${p.slug}`} className="p-2 rounded-lg text-sky-600 hover:text-white hover:bg-sky-500"><Edit2 size={13} /></button>
                          <button onClick={() => remove(p)} title="Delete" data-testid={`blog-delete-${p.slug}`} className="p-2 rounded-lg text-rose-600 hover:text-white hover:bg-rose-500"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!filtered.length && <tr><td colSpan={7} className="py-16 text-center text-slate-400 text-sm">No posts match.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition group" data-testid={`blog-card-${p.slug}`}>
                <div className="aspect-video bg-gradient-to-br from-fuchsia-100 via-sky-100 to-emerald-100 flex items-center justify-center">
                  {p.hero_image ? <img src={p.hero_image} alt={p.title} className="w-full h-full object-cover" /> : <ImageIcon size={30} className="text-slate-300" />}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-wider bg-slate-100 text-slate-600">{p.category || "—"}</span>
                    <span className={`text-[9px] px-2 py-0.5 rounded font-black uppercase tracking-wider ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.published ? "PUBLISHED" : "DRAFT"}</span>
                  </div>
                  <h3 className="text-sm font-black text-slate-900 line-clamp-2">{p.title}</h3>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{p.excerpt}</p>
                  <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                    <span>{p.read_time || readMinutes(p.content)} min · {p.views || 0} views</span>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button onClick={() => startEdit(p)} className="p-1 rounded text-sky-600 hover:bg-sky-50"><Edit2 size={12} /></button>
                      <button onClick={() => remove(p)} className="p-1 rounded text-rose-600 hover:bg-rose-50"><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {!filtered.length && <div className="col-span-full py-16 text-center text-slate-400 text-sm">No posts.</div>}
          </div>
        )}

        <style>{`.inp{width:100%;height:42px;padding:0 14px;border-radius:8px;border:1px solid #e2e8f0;font-size:14px;outline:none;background:white}.inp:focus{border-color:#636AC3;box-shadow:0 0 0 3px rgba(99,106,195,0.15)}`}</style>
      </div>
    </AdminLayout>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-black uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
function RoadmapTile({ label, done, total, color }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
      <div className={`text-2xl font-black font-mono tabular-nums ${color}`}>{done}<span className="text-slate-400 text-base">/{total}</span></div>
      <div className="text-[10px] font-black uppercase tracking-wider text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}
function MiniStat({ label, value, icon: Icon, tone }) {
  const map = { slate: "bg-slate-100 text-slate-700", emerald: "bg-emerald-100 text-emerald-700", amber: "bg-amber-100 text-amber-700", fuchsia: "bg-indigo-100 text-indigo-700" }[tone];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${map}`}><Icon size={15} strokeWidth={2.5} /></div>
      <div>
        <div className="text-2xl font-black text-slate-900 tabular-nums">{value}</div>
        <div className="text-[10px] font-black uppercase tracking-wider text-slate-500">{label}</div>
      </div>
    </div>
  );
}
